import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import GetCareServices from '../../../api/services/GetCareServices';
import { EntryResource, ServiceTypes, VisitType } from '../Components/select-service-type/types';
import { RootState } from '../../../store';
import { PaymentMethodsResponse } from '../Components/select-payment-method/types';
import { CommonDropdownType } from '../../../types';
import { AccountUser, Member, MemberPlanElement } from '../../account/types';
import { VisitBookingResponse } from '../../../api/types';
import { generateBoilerplateAsyncThunkMatchers } from '../../../store/helpers';
import { Visit } from '../types';

type GetCareState = {
  selectedPatient: AccountUser | Member | null;
  serviceTypes: ServiceTypes | null;
  selectedServiceType: EntryResource | null;
  selectedPaymentMethod: PaymentMethodsResponse | null;
  selectedVisitState: CommonDropdownType | null;
  selectedVisitType: VisitType | null;
  selectedVisitPlan: MemberPlanElement | null;
  selectedVisitReason: CommonDropdownType | null;
  selectedPhoneNumber: string | null;
  visit: VisitBookingResponse | null;
  waitingRoomVisit: Visit | null;
  chiefComplaintReference: string | null;
  callbackNumberReference: string | null;
  status: 'pending' | 'success' | 'failure';
  isLoading: boolean;
  isSubmitting: boolean;
};

const initialState: GetCareState = {
  selectedPatient: null,
  serviceTypes: null,
  selectedServiceType: null,
  selectedPaymentMethod: null,
  selectedVisitState: null,
  selectedVisitType: null,
  selectedVisitPlan: null,
  selectedVisitReason: null,
  selectedPhoneNumber: null,
  visit: null,
  waitingRoomVisit: null,
  chiefComplaintReference: null,
  callbackNumberReference: null,
  status: 'pending',
  isLoading: false,
  isSubmitting: false,
};

const ACTIONS_PREFIX = 'getCare';

// Async Thunks
export const getServiceTypeThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/getServiceTypeThunk`,
  GetCareServices.getServiceTypes,
);
export const insertUpdateVisitThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/insertUpdateVisitThunk`,
  GetCareServices.insertUpdateVisit,
);
export const startPaymentIntentThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/startPaymentIntentThunk`,
  GetCareServices.startPaymentIntent,
);

const getCareSlice = createSlice({
  name: 'getCare',
  initialState,
  reducers: {
    setSelectedPatient: (state, action: PayloadAction<AccountUser | Member>) => {
      state.selectedPatient = action.payload;
    },
    setSelectedServiceType: (state, action: PayloadAction<EntryResource>) => {
      state.selectedServiceType = action.payload;
    },
    setSelectedPaymentMethod: (state, action: PayloadAction<PaymentMethodsResponse>) => {
      state.selectedPaymentMethod = action.payload;
    },
    setSelectedVisitState: (state, action: PayloadAction<CommonDropdownType>) => {
      state.selectedVisitState = action.payload;
    },
    setSelectedVisitType: (state, action: PayloadAction<VisitType>) => {
      state.selectedVisitType = action.payload;
    },
    setSelectedVisitPlan: (state, action: PayloadAction<MemberPlanElement>) => {
      state.selectedVisitPlan = action.payload;
    },
    setWaitingRoomVisit: (state, action: PayloadAction<Visit>) => {
      state.waitingRoomVisit = action.payload;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setIsSubmitting: (state, action) => {
      state.isSubmitting = action.payload;
    },
    setCallbackNumberReference: (state, action) => {
      state.callbackNumberReference = action.payload;
    },
    setChiefComplaintReference: (state, action) => {
      state.chiefComplaintReference = action.payload;
    },
    setSelectedVisitReason: (state, action: PayloadAction<CommonDropdownType>) => {
      state.selectedVisitReason = action.payload;
    },
    setSelectedPhoneNumber: (state, action: PayloadAction<string | null>) => {
      state.selectedPhoneNumber = action.payload;
    },
    clearGetCareState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getServiceTypeThunk.fulfilled, (state, action) => {
      const serviceTypes = action.payload?.resource as ServiceTypes;
      state.serviceTypes = serviceTypes ?? null;
    });
    builder.addCase(insertUpdateVisitThunk.fulfilled, (state, action) => {
      const visit = action.payload as VisitBookingResponse;
      state.visit = visit;
    });
    generateBoilerplateAsyncThunkMatchers<GetCareState>(builder, ACTIONS_PREFIX);
  },
});

// SELECTORS
export const getCareState = (state: RootState) => state.getCare;

// ACTIONS
export const {
  setSelectedPatient,
  setSelectedServiceType,
  setSelectedPaymentMethod,
  setSelectedVisitState,
  setSelectedVisitType,
  setSelectedVisitPlan,
  setSelectedVisitReason,
  setSelectedPhoneNumber,
  setIsLoading,
  setIsSubmitting,
  clearGetCareState,
  setCallbackNumberReference,
  setChiefComplaintReference,
  setWaitingRoomVisit,
} = getCareSlice.actions;

export default getCareSlice.reducer;
